import type { ICmsLink, Media } from "@mw/shared/payload-types";
import type { VariantProps } from "class-variance-authority";

import { Image } from "@components/core/Image";
import { ImageSizes } from "@ui/lib/image";
import { cn } from "@ui/lib/utils";
import { cva } from "class-variance-authority";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";
import { CardLinkArrow } from "@ui/components/core/CardLinkArrow";
import Link from "next/link";
import { getLinkProps } from "@utilities/payload";
import clsx from "clsx";
import { bentoCardVariants } from "@/app/_blocks/BlockBentoGrid/sharedCardVariants";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import IconCheckmark1 from "@ui/central-icons/icons/IconCheckmark1";

// Z-index constant for the card link overlay
const CARD_LINK_Z_INDEX = "tw-z-10";

export type BenefitItem = {
  icon: Media;
  text: string;
};

export type CardContentType =
  | {
      image?: Media;
      imageFit?: "contain" | "cover" | null;
      imageLayout?: "center" | "stretch" | null;
      type: "image";
    }
  | {
      items?: BenefitItem[] | null;
      type: "benefits";
    };

export const gridCardVariants = cva("flex flex-col justify-between pb-0 gap-100 h-full", {
  variants: {
    colSpan: {
      "1": "col-span-1",
      "2": "col-span-1 md:col-span-2 lg:col-span-2",
    },
    theme: {
      gray: "tw-bg-cream",
      white: "bg-default",
      black: "bg-inverse-light",
    },
    padding: {
      default: "p-36 pb-0 xl:p-52 xl:pb-0",
      small: "p-16 pb-0 xl:p-32 xl:pb-0",
      none: "p-0 pb-0",
    },
    largePadding: {
      larger: "",
      regular: "",
    },
  },
  compoundVariants: [
    {
      colSpan: "2",
      padding: "default",
      largePadding: "larger",
      className: "pb-36 xl:pb-120 xl:p-120",
    },
    {
      colSpan: "2",
      padding: "small",
      largePadding: "larger",
      className: "pb-32 xl:pb-32 lg:col-span-2 xl:p-32",
    },
  ],
  defaultVariants: {
    padding: "default",
    largePadding: "larger",
  },
});

export type GridCardProps = VariantProps<typeof gridCardVariants> & {
  children: React.ReactNode;
  className?: string;
  theme?: "black" | "gray" | "white" | null;
  hasLink?: boolean;
};

export const GridCard = ({ children, className, colSpan, theme, padding, largePadding, hasLink }: GridCardProps) => {
  return (
    <div
      className={cn(
        bentoCardVariants({ background: "none" }),
        gridCardVariants({ colSpan, theme, padding, largePadding }),
        hasLink && "tw-group has-[[data-card-link]:focus-visible]:tw-outline-focus",
        className,
      )}
    >
      {children}
      <HairlineCardBorder roundedClassName='tw-rounded-2.5xl' />
    </div>
  );
};

export const gridCardHeaderVariants = cva("", {
  variants: {
    padding: {
      default: "p-36 xl:p-52 pb-0 xl:pb-0",
      small: "p-16 xl:p-32 pb-0 xl:pb-0",
      none: "p-0",
    },
  },
});

export interface GridCardHeaderProps extends VariantProps<typeof gridCardHeaderVariants> {
  title: string;
  description: string;
  inverse?: boolean;
  titleSize?: "default" | "medium" | null;
  link?: ICmsLink | null;
}

export const GridCardHeader = ({
  title,
  description,
  inverse,
  padding,
  titleSize = "default",
  link,
}: GridCardHeaderProps) => {
  const linkProps = getLinkProps(link);
  const titleColorClass =
    titleSize === "medium" ? (inverse ? "text-inverse" : "tw-text-black") : inverse ? "text-inverse" : undefined;

  return (
    <div className={gridCardHeaderVariants({ padding })}>
      <h3 className={clsx("mb-16", titleSize === "medium" ? "tw-type-xl" : "f-heading-04", titleColorClass)}>
        {linkProps ? (
          <Link {...linkProps} className='tw-outline-none tw-inline-flex tw-items-center tw-gap-1' data-card-link>
            {title}
            <CardLinkArrow className={titleColorClass} />
            <span className={cn("tw-absolute tw-inset-0", CARD_LINK_Z_INDEX)} />
          </Link>
        ) : (
          title
        )}
      </h3>
      <div
        className={cn(
          titleSize === "medium"
            ? ["tw-type-base", inverse ? "text-inverse-dark" : "tw-text-gray-500"]
            : ["f-description-03", inverse ? "text-inverse-dark" : "text-light"],
        )}
      >
        {description}
      </div>
    </div>
  );
};

export const BenefitsList = ({ items }: { items?: BenefitItem[] | null }) => {
  if (!items?.length) return null;

  const firstThreeItems = items.slice(0, 3);
  const remainingItems = items.slice(3);

  return (
    <div className='w-full'>
      {/* First 3 items in 3-column layout */}
      <div className='flex w-full flex-wrap gap-y-16 md:gap-y-32'>
        {firstThreeItems.map((item, index) => (
          <div className='flex basis-full items-center gap-8 md:basis-1/3 md:justify-center' key={index}>
            <div className='flex-shrink-0'>
              <Image className='h-24 w-24 lg:h-40 lg:w-40' resource={item.icon} sizes={ImageSizes.FULL_1_3_ON_LG} />
            </div>
            <span className='f-subhead-02 mr-12'>{item.text}</span>
          </div>
        ))}
      </div>

      {/* Remaining items in 2-column layout */}
      {remainingItems.length > 0 && (
        <div className='mt-16 grid w-2/3 grid-cols-1 gap-16 gap-y-16 md:mx-auto md:mt-32 md:grid-cols-2 md:gap-y-32'>
          {remainingItems.map((item, index) => (
            <div className='flex items-center gap-8 md:justify-center' key={index + 3}>
              <div className='flex-shrink-0'>
                <Image className='h-24 w-24 lg:h-40 lg:w-40' resource={item.icon} sizes={ImageSizes.FULL_1_3_ON_LG} />
              </div>
              <span className='f-subhead-02'>{item.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const CardContent = ({ content }: { content: CardContentType }) => {
  if (content.type === "image") {
    return <Image fit={content.imageFit} resource={content.image} sizes={ImageSizes.FULL_HALF_ON_LG} />;
  } else if (content.type === "benefits") {
    return <BenefitsList items={content.items} />;
  }

  return null;
};

export const ChecklistItems = ({ items }: { items?: Array<{ text: string }> | null }) => {
  if (!items?.length) return null;

  return (
    <ul role='list' className='-tw-my-3'>
      {items.map((item, index) => (
        <li key={index}>
          <div className='tw-flex tw-gap-3 tw-py-3'>
            <IconCheckmark1 className={clsx(iconStrokeFix, "tw-size-4 tw-shrink-0 tw-text-gray-500")} aria-hidden />
            <span className='tw-mt-0.75 tw-type-xs tw-text-gray-500'>{item.text}</span>
          </div>
          {index < items.length - 1 && <DottedBorder side='inline' />}
        </li>
      ))}
    </ul>
  );
};

export const cardContentVariants = cva("z-1 flex justify-between", {
  variants: {
    contentGap: {
      default: "gap-80",
      small: "gap-40",
      none: "gap-0",
    },
    height: {
      full: "h-full",
      auto: "h-auto",
    },
    layout: {
      vertical: "flex-col",
      horizontal: "flex-col lg:flex-row",
    },
  },
  defaultVariants: {
    contentGap: "default",
    height: "full",
    layout: "vertical",
  },
});
